import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Mail, Ban, CheckCircle2, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { formatDate } from '@/utils/cn';

export function AdminPartnersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin-partners', search],
    queryFn: () => apiClient.admin.listPartners({ search: search || undefined, limit: 100 }).then(r => r.data),
  });

  const resendMut = useMutation({
    mutationFn: (id: number) => apiClient.admin.resendPartnerInvite(id),
    onSuccess: () => toast.success('Convite reenviado.'),
    onError: () => toast.error('Falha ao reenviar convite.'),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => apiClient.admin.updatePartner(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-partners'] }); toast.success('Status atualizado.'); },
    onError: () => toast.error('Falha ao atualizar.'),
  });

  return (
    <div className="container-app py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Users className="h-7 w-7 text-brand-600" /> Parceiros</h1>
          <p className="text-muted-foreground">Revendas que cadastram e acompanham clientes</p>
        </div>
        <Button onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-1" /> Novo parceiro</Button>
      </div>

      <div className="relative max-w-md mt-6 mb-6">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar (nome, e-mail)..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase">Parceiro</th>
              <th className="p-3 text-xs uppercase">Contato</th>
              <th className="p-3 text-xs uppercase">Status</th>
              <th className="p-3 text-xs uppercase">Clientes</th>
              <th className="p-3 text-xs uppercase">Último acesso</th>
              <th className="p-3 text-xs uppercase">Cadastro</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((p: any) => (
              <tr key={p.id} className="border-b border-border/50 hover:bg-muted/30">
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3">
                  <div>{p.email}</div>
                  {p.phone && <div className="text-xs text-muted-foreground">{p.phone}</div>}
                </td>
                <td className="p-3">
                  <Badge variant={p.status === 'active' ? 'success' : p.status === 'pending' ? 'default' : 'danger'}>{p.status}</Badge>
                </td>
                <td className="p-3">{p.client_count}</td>
                <td className="p-3">{p.last_login_at ? formatDate(p.last_login_at) : <span className="text-muted-foreground">nunca</span>}</td>
                <td className="p-3">{formatDate(p.created_at)}</td>
                <td className="p-3 flex gap-1 flex-wrap">
                  <Button size="sm" variant="ghost" title="Reenviar convite" disabled={resendMut.isPending} onClick={() => resendMut.mutate(p.id)}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> Convite
                  </Button>
                  {p.status === 'blocked'
                    ? <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => statusMut.mutate({ id: p.id, status: 'active' })}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Reativar</Button>
                    : <Button size="sm" variant="ghost" className="text-red-600" onClick={() => statusMut.mutate({ id: p.id, status: 'blocked' })}><Ban className="h-3.5 w-3.5 mr-1" /> Bloquear</Button>}
                </td>
              </tr>
            ))}
            {data?.items?.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhum parceiro ainda.</td></tr>
            )}
          </tbody>
        </table>
        <div className="p-3 text-xs text-muted-foreground border-t border-border">{data?.total ?? 0} parceiro(s)</div>
      </Card>

      {showNew && <NewPartnerModal onClose={() => setShowNew(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['admin-partners'] })} />}
    </div>
  );
}

function NewPartnerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const createMut = useMutation({
    mutationFn: () => apiClient.admin.createPartner({
      name: form.name.trim(), email: form.email.trim(),
      phone: form.phone.trim() || undefined, notes: form.notes.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success('Parceiro cadastrado! Enviamos o convite por e-mail.');
      onCreated();
      onClose();
    },
    onError: (e: any) => {
      if (e?.response?.data?.error === 'email_taken') toast.error('Já existe um parceiro com este e-mail.');
      else toast.error(e?.response?.data?.message ?? 'Falha ao cadastrar parceiro.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold">Novo parceiro</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Nome *</label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">E-mail *</label>
            <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Telefone / WhatsApp</label>
            <Input value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Observações</label>
            <Input value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">O parceiro recebe um e-mail pra definir a senha e acessar o painel dele.</p>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/10">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => createMut.mutate()} disabled={!valid || createMut.isPending}>
            {createMut.isPending ? 'Cadastrando...' : 'Cadastrar e convidar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
